import { Injectable } from '@angular/core';
import { SyllabusHttpModel, SyllabusQuery } from '@examdojo/category';
import { Query } from '@examdojo/state';
import { UserQuery } from '@examdojo/user';
import { combineLatest, map, Observable } from 'rxjs';
import { REVIEW_PERMISSIONS, ReviewPermission } from './review-permissions.model';
import { ReviewPermissionsState, ReviewPermissionsStore } from './review-permissions.store';

@Injectable({ providedIn: 'root' })
export class ReviewPermissionsQuery extends Query<ReviewPermissionsState> {
  constructor(
    protected override readonly store: ReviewPermissionsStore,
    private readonly userQuery: UserQuery,
    private readonly syllabusQuery: SyllabusQuery,
  ) {
    super(store);
  }

  readonly NO_PERMISSIONS = REVIEW_PERMISSIONS.reduce(
    (acc, key) => {
      acc[key as ReviewPermission] = false;
      return acc;
    },
    {} as Record<ReviewPermission, boolean>,
  );

  readonly ALL_PERMISSIONS = REVIEW_PERMISSIONS.reduce(
    (acc, key) => {
      acc[key as ReviewPermission] = true;
      return acc;
    },
    {} as Record<ReviewPermission, boolean>,
  );

  hasReviewPermission(syllabusId: SyllabusHttpModel['id'], role: ReviewPermission): boolean {
    return (
      (this.userQuery.hasRoleOrHigher('ADMIN') ||
        this.getValue().reviewPermissions?.some(
          (permission) => permission.syllabus_id === syllabusId && permission[role] === true,
        )) ??
      false
    );
  }

  getAllowedSyllabuses(): Array<SyllabusHttpModel['id']> {
    const syllabuses = new Set<SyllabusHttpModel['id']>();

    if (this.userQuery.hasRoleOrHigher('ADMIN')) {
      return this.syllabusQuery.getAllEntities().map((s) => s.id);
    }

    this.getValue()
      .reviewPermissions.filter((p) => REVIEW_PERMISSIONS.some((reviewPermission) => p[reviewPermission] === true))
      .forEach((p) => {
        syllabuses.add(p.syllabus_id);
      });

    return Array.from(syllabuses);
  }

  /**
   * Returns the syllabuses where the user has some permissions.
   * If the user is an admin, returns all syllabuses.
   * If the user is not an admin, returns the syllabuses where the user has some permissions.
   * If the user has no permissions, returns an empty array.
   */
  selectAllowedSyllabuses(): Observable<Array<SyllabusHttpModel['id']>> {
    return combineLatest([
      this.select('reviewPermissions'),
      this.userQuery.selectHasRole('ADMIN'),
      this.syllabusQuery.selectAllEntities(),
    ]).pipe(
      map(([reviewPermissions, isAdmin, allSyllabuses]) => {
        if (isAdmin) {
          return allSyllabuses.map((s) => s.id);
        }

        const syllabuses = new Set<SyllabusHttpModel['id']>();
        reviewPermissions
          ?.filter((p) => REVIEW_PERMISSIONS.some((reviewPermission) => p[reviewPermission] === true))
          .forEach((p) => {
            syllabuses.add(p.syllabus_id);
          });

        return Array.from(syllabuses);
      }),
    );
  }

  selectSyllabusPermissions(syllabusId: SyllabusHttpModel['id']): Observable<Record<ReviewPermission, boolean>> {
    return combineLatest([this.select('reviewPermissions'), this.userQuery.selectHasRole('ADMIN')]).pipe(
      map(([reviewPermissions, isAdmin]) => {
        if (isAdmin) {
          return this.ALL_PERMISSIONS;
        }

        const permission = reviewPermissions?.find((p) => p.syllabus_id === syllabusId);
        const result = {} as Record<ReviewPermission, boolean>;

        for (const key of REVIEW_PERMISSIONS) {
          result[key] = permission?.[key] ?? false;
        }

        return result;
      }),
    );
  }

  getSyllabusPermissions(syllabusId: SyllabusHttpModel['id']): ReviewPermission[] {
    if (this.userQuery.hasRoleOrHigher('ADMIN')) {
      return REVIEW_PERMISSIONS;
    }

    const permissions = new Set<ReviewPermission>();

    this.getValue().reviewPermissions.forEach((p) => {
      if (p.syllabus_id === syllabusId) {
        Object.keys(p).forEach((key) => {
          if (REVIEW_PERMISSIONS.includes(key as ReviewPermission)) {
            permissions.add(key as ReviewPermission);
          }
        });
      }
    });

    return Array.from(permissions);
  }
}
